import {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
  type ReactNode,
} from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider,
  signOut as firebaseSignOut,
  type User as FirebaseUser,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db } from "../lib/firebase";
import { activateAccount } from "../services/userManagementService";

type AccountStatus = "loading" | "active" | "needs-setup" | "no-access";

interface AuthContextValue {
  user: FirebaseUser | null;
  loading: boolean;
  role: string | null;
  /** ADR-137: caller's personId from auth token custom claim, with
   * authAccounts/{uid} fallback for stale tokens. */
  personId: string | null;
  isOrganizerOrAdmin: boolean;
  accountStatus: AccountStatus;
  markSetupComplete: () => void;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithGoogle: () => Promise<void>;
  signInWithMicrosoft: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  loading: true,
  role: null,
  personId: null,
  isOrganizerOrAdmin: false,
  accountStatus: "loading",
  markSetupComplete: () => {},
  signIn: async () => {},
  signInWithGoogle: async () => {},
  signInWithMicrosoft: async () => {},
  signOut: async () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

/**
 * Resolves the full auth state for a Firebase user: reads role from custom
 * claims, activates account from a staged person if no `authAccounts/{uid}`
 * pointer exists yet (organizer/admin first-login path OR wipe-then-redeploy
 * recovery), and decides whether the dashboard should let them in, prompt
 * them through `AccountSetupPage`, or refuse.
 *
 * `activateAccount` is called whenever the `authAccounts/{uid}` pointer is
 * missing, regardless of role-claim presence. The previous gate of "only
 * when role claim is absent" missed the wipe-then-redeploy scenario: an
 * operator whose persons + authAccounts row was wiped from Firestore but
 * whose Auth account + role claim survived would be silently dead-ended on
 * AccountSetupPage. `activateAccount` is idempotent — it short-circuits if
 * the pointer already exists.
 *
 * ADR-136: gate state on `authAccounts/{uid}` — the thin uid->personId
 * pointer that exists iff the user has been claimed. `profileReviewed`
 * lives here too (set true after first-login profile review).
 */
async function resolveAuthState(firebaseUser: FirebaseUser) {
  let tokenResult = await firebaseUser.getIdTokenResult();
  let userRole = (tokenResult.claims.role as string) ?? null;
  let personId = (tokenResult.claims.personId as string) ?? null;

  const authAccountDoc = await getDoc(
    doc(db, "authAccounts", firebaseUser.uid),
  );
  let authAccountExists = authAccountDoc.exists();

  if (!authAccountExists) {
    try {
      const result = await activateAccount();
      if (result.matched && !result.alreadySetUp) {
        await firebaseUser.getIdToken(true);
        tokenResult = await firebaseUser.getIdTokenResult();
        userRole = (tokenResult.claims.role as string) ?? null;
        personId = (tokenResult.claims.personId as string) ?? null;
      }
      if (result.matched) {
        authAccountExists = true;
      }
    } catch (error) {
      // activateAccount failed — typically "no matching person", but
      // could be a transient Functions error. Log so we don't silently
      // route real failures to no-access.
      console.error("[auth] activateAccount failed:", error);
    }
  }

  if (!userRole || (userRole !== "organizer" && userRole !== "admin")) {
    return {
      userRole,
      personId,
      accountStatus: "no-access" as AccountStatus,
    };
  }

  // Re-read authAccounts/{uid} after potential bootstrap so we see the
  // freshly-minted profileReviewed value rather than the stale pre-call snapshot.
  const finalAuthAccountDoc = authAccountExists
    ? await getDoc(doc(db, "authAccounts", firebaseUser.uid))
    : authAccountDoc;
  const accountStatus: AccountStatus =
    finalAuthAccountDoc.exists() &&
    finalAuthAccountDoc.data()?.profileReviewed === true
      ? "active"
      : "needs-setup";

  // ADR-137 transitional fallback: a user whose token was issued before the
  // personId-claim deploy will lack the claim until their next force-refresh.
  // Fall back to the authAccounts pointer for those cases. Token claim is
  // preferred when present (zero-read path).
  if (!personId && finalAuthAccountDoc.exists()) {
    personId = (finalAuthAccountDoc.data()?.personId as string) ?? null;
  }

  // ADR-191: admins need no per-event rows; the admin claim satisfies
  // every event-scoped security rule. (ADR-063's syncAdminMemberships
  // call that used to run here is retired.)

  return { userRole, personId, accountStatus };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<FirebaseUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [role, setRole] = useState<string | null>(null);
  const [personId, setPersonId] = useState<string | null>(null);
  const [accountStatus, setAccountStatus] = useState<AccountStatus>("loading");

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (!firebaseUser) {
        setUser(null);
        setRole(null);
        setPersonId(null);
        setAccountStatus("loading");
        setLoading(false);
        return;
      }

      setLoading(true);
      const {
        userRole,
        personId: resolvedPersonId,
        accountStatus: status,
      } = await resolveAuthState(firebaseUser);
      setUser(firebaseUser);
      setRole(userRole);
      setPersonId(resolvedPersonId);
      setAccountStatus(status);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    await signInWithEmailAndPassword(auth, email, password);
  }, []);

  const signInWithGoogleFn = useCallback(async () => {
    const provider = new GoogleAuthProvider();
    // Force the account picker every sign-in, even when a session is cached.
    provider.setCustomParameters({ prompt: "select_account" });
    await signInWithPopup(auth, provider);
  }, []);

  const signInWithMicrosoftFn = useCallback(async () => {
    const provider = new OAuthProvider("microsoft.com");
    const tenantId = import.meta.env.VITE_MICROSOFT_TENANT_ID;
    // `prompt: select_account` forces Microsoft to show the account picker
    // with remembered accounts instead of auto-signing in with a cached session.
    provider.setCustomParameters({
      prompt: "select_account",
      ...(tenantId ? { tenant: tenantId } : {}),
    });
    await signInWithPopup(auth, provider);
  }, []);

  const signOutFn = useCallback(async () => {
    await firebaseSignOut(auth);
  }, []);

  const isOrganizerOrAdmin = role === "organizer" || role === "admin";

  const markSetupComplete = useCallback(() => {
    setAccountStatus("active");
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      role,
      personId,
      isOrganizerOrAdmin,
      accountStatus,
      markSetupComplete,
      signIn,
      signInWithGoogle: signInWithGoogleFn,
      signInWithMicrosoft: signInWithMicrosoftFn,
      signOut: signOutFn,
    }),
    [
      user,
      loading,
      role,
      personId,
      isOrganizerOrAdmin,
      accountStatus,
      markSetupComplete,
      signIn,
      signInWithGoogleFn,
      signInWithMicrosoftFn,
      signOutFn,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
